import React, { useState } from "react";
import { motion } from "framer-motion";
import { Scale, BookOpen, ShieldAlert, Award, FileSpreadsheet, KeyRound, HelpCircle } from "lucide-react";
import Navbar from "../components/landing/Navbar";
import SignupDialog from "../components/landing/SignupDialog";

// Soft mesh gradients background matching the STUDLYF style
const PurpleFloatingBlobs = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden z-0">
    <motion.div
      animate={{ x: [0, -30, 20, 0], y: [0, 20, -20, 0], scale: [1, 0.95, 1.05, 1] }}
      transition={{ duration: 18, repeat: Infinity, ease: "easeInOut" }}
      className="absolute top-[-5%] right-[-5%] w-[400px] h-[400px] rounded-full"
      style={{ background: "radial-gradient(circle, rgba(124, 58, 237, 0.16) 0%, transparent 70%)", filter: "blur(80px)" }}
    />
    <motion.div
      animate={{ x: [0, 20, -10, 0], y: [0, -30, 10, 0], scale: [1, 1.05, 0.95, 1] }}
      transition={{ duration: 24, repeat: Infinity, ease: "easeInOut" }}
      className="absolute top-[25%] left-[-5%] w-[450px] h-[450px] rounded-full"
      style={{ background: "radial-gradient(circle, rgba(236, 72, 153, 0.14) 0%, transparent 70%)", filter: "blur(85px)" }}
    />
  </div>
);

export default function TermsConditionsPage() {
  const [signupOpen, setSignupOpen] = useState(false);
  const [authMode, setAuthMode] = useState("signup");

  const openSignup = () => { setAuthMode("signup"); setSignupOpen(true); };
  const openLogin = () => { setAuthMode("login"); setSignupOpen(true); };

  return (
    <div className="relative min-h-screen flex flex-col flex-1 w-full bg-[#FAFAFC] dark:bg-gray-950 text-gray-900 dark:text-gray-100 overflow-x-hidden font-sans">
      <Navbar onGetStarted={openSignup} onLogin={openLogin} />
      <PurpleFloatingBlobs />
      
      <main className="relative z-10 pt-28 pb-16 flex-1 w-full px-4 sm:px-5 md:px-8" data-testid="terms-and-conditions-page">
        {/* Header Section */}
        <div className="text-center mb-12">
          <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/70 dark:bg-gray-900/50 backdrop-blur-md border border-gray-200/50 dark:border-gray-800/50 text-xs font-semibold text-[#7C3AED] dark:text-[#8C85FF] w-fit mx-auto mb-4">
            <Scale className="w-3.5 h-3.5" />
            Terms of Service
          </div>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tight text-gray-900 dark:text-white leading-tight">
            Terms & <span className="brand-gradient-text">Conditions</span>
          </h1>
          <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">
            Last Updated: July 20, 2026 • Please read these terms carefully before using our platform.
          </p>
        </div>

        {/* Content Box */}
        <div className="relative bg-white/80 dark:bg-gray-900/80 backdrop-blur-2xl border border-gray-100 dark:border-gray-800/80 rounded-[32px] p-8 md:p-12 shadow-[0_8px_32px_rgba(0,0,0,0.02)] dark:shadow-[0_8px_32px_rgba(0,0,0,0.3)]">
          <div className="space-y-8">
            
            {/* 1. Acceptance of Terms */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <BookOpen className="w-5 h-5 text-[#7C3AED]" />
                1. Acceptance of Terms
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                By accessing, browsing, or using the STUDLYF AI platform, website, or associated services (collectively, the "Service"), you acknowledge that you have read, understood, and agree to be bound by these Terms & Conditions ("Terms"), along with our Privacy Policy. If you do not agree with any part of these Terms, you must immediately cease all access and use of the Service.
              </p>
            </section>

            {/* 2. User Responsibilities */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <ShieldAlert className="w-5 h-5 text-[#7C3AED]" />
                2. User Responsibilities
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                As a user of the Service, you agree to:
              </p>
              <ul className="mt-2 list-disc pl-5 text-gray-600 dark:text-gray-300 text-sm space-y-1.5">
                <li>Provide accurate, current, and complete information during registration.</li>
                <li>Maintain the confidentiality of your account credentials and restrict unauthorized access to your account.</li>
                <li>Accept sole responsibility for all activities that occur under your account.</li>
                <li>Refrain from engaging in any activity that disrupts or interferes with the performance, integrity, or security of the Service.</li>
              </ul>
            </section>

            {/* 3. Account Usage */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <KeyRound className="w-5 h-5 text-[#7C3AED]" />
                3. Account Usage & Registration
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                STUDLYF AI provides workspaces for student builders, startup teams, mentors, agencies, and investors. Accounts are subject to the following rules:
              </p>
              <ul className="mt-2 list-disc pl-5 text-gray-600 dark:text-gray-300 text-sm space-y-1.5">
                <li>You must be at least 18 years old or possess legal parental/guardian consent to use this Service.</li>
                <li>You may not use our Service for any illegal or unauthorized purpose, nor violate any laws in your jurisdiction.</li>
                <li>We reserve the right to suspend or terminate accounts that violate our community standards or these Terms without prior notice.</li>
              </ul>
            </section>

            {/* 4. Subscription & Payments */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <FileSpreadsheet className="w-5 h-5 text-[#7C3AED]" />
                4. Subscription & Payments
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                Certain premium modules, templates, and analytics may require paid subscriptions. Subscription options, pricing tiers, and billing frequencies are outlined on the Pricing page. All fees are non-refundable unless specified otherwise. We reserve the right to modify pricing or introduce new charges at any time, with appropriate notification to active subscribers.
              </p>
            </section>

            {/* 5. Intellectual Property */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <Award className="w-5 h-5 text-[#7C3AED]" />
                5. Intellectual Property Rights
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                All content, designs, algorithms, code, graphics, branding, and interfaces on the STUDLYF AI platform are the intellectual property of STUDLYF.
              </p>
              <p className="mt-2 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                You retain ownership of the data, ideas, and documents you input into your Workspace. However, by using the Service, you grant us a limited license to process and handle this content solely to provide the services and train your localized AI agents.
              </p>
            </section>

            {/* 6. Privacy */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <KeyRound className="w-5 h-5 text-[#7C3AED]" />
                6. Privacy Policy Integration
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                Your use of the Service is also governed by our Privacy Policy, which describes how we collect, use, and store your personal information. By agreeing to these Terms, you consent to the collection and use of your data as outlined in our Privacy Policy.
              </p>
            </section>

            {/* 7. Limitation of Liability */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <ShieldAlert className="w-5 h-5 text-[#7C3AED]" />
                7. Limitation of Liability
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                To the maximum extent permitted by applicable law, STUDLYF AI shall not be liable for any indirect, incidental, special, consequential, or punitive damages, or any loss of profits, revenues, data, use, goodwill, or other intangible losses resulting from (i) your access to or use of or inability to access or use the Service; (ii) any conduct or content of any third party on the Service; or (iii) unauthorized access, use, or alteration of your content or data.
              </p>
            </section>

            {/* 8. Termination */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <Scale className="w-5 h-5 text-[#7C3AED]" />
                8. Termination
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                We may terminate or suspend your account and bar access to the Service immediately, without prior notice or liability, under our sole discretion, for any reason whatsoever and without limitation, including but not limited to a breach of these Terms. If you wish to terminate your account, you may simply discontinue using the Service.
              </p>
            </section>

            {/* 9. Governing Law */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <Scale className="w-5 h-5 text-[#7C3AED]" />
                9. Governing Law
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                These Terms shall be governed and construed in accordance with the laws of India, without regard to its conflict of law provisions. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights.
              </p>
            </section>

            {/* 10. Contact Information */}
            <section>
              <h2 className="flex items-center gap-2.5 text-xl font-bold text-gray-900 dark:text-white border-b border-gray-100 dark:border-gray-850 pb-2 font-display">
                <HelpCircle className="w-5 h-5 text-[#7C3AED]" />
                10. Contact Information
              </h2>
              <p className="mt-3 text-gray-600 dark:text-gray-300 text-sm leading-relaxed">
                If you have any questions about these Terms, please contact us at:
              </p>
              <div className="mt-4 p-4 rounded-2xl bg-gray-50 dark:bg-gray-950/40 border border-gray-150 dark:border-gray-800 text-sm">
                <p className="font-semibold text-gray-900 dark:text-white">STUDLYF AI Support Team</p>
                <p className="text-gray-500 dark:text-gray-400 mt-1">Email: <a href="mailto:studlyf21@gmail.com" className="text-[#7C3AED] hover:underline font-semibold">studlyf21@gmail.com</a></p>
                <p className="text-gray-500 dark:text-gray-400">Address: Ecosystem Innovation Lab, India</p>
              </div>
            </section>

          </div>
        </div>
      </main>

      <SignupDialog open={signupOpen} onOpenChange={setSignupOpen} initialMode={authMode} />
    </div>
  );
}
